import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, MapPin, DollarSign } from "lucide-react";

export function ZonesSection() {
  const [rows, setRows] = useState<any[]>([]);
  const [form, setForm] = useState({ code: "", name: "" });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  // Pricing state
  const [selectedZone, setSelectedZone] = useState<any | null>(null);
  const [pricingRows, setPricingRows] = useState<any[]>([]);
  const [serviceTypes, setServiceTypes] = useState<any[]>([]);
  const [pricingForm, setPricingForm] = useState({ service_type_id: "", price: "", max_passengers: "4", extra_cost_per_pax: "0" });
  const [pricingEditingId, setPricingEditingId] = useState<string | null>(null);
  const [isPricingFormOpen, setIsPricingFormOpen] = useState(false);
  const [pricingDeleteId, setPricingDeleteId] = useState<string | null>(null);
  const [pricingCounts, setPricingCounts] = useState<Record<string, number>>({});

  const { toast } = useToast();

  const load = async () => {
    const [z, st, pc] = await Promise.all([
      supabase.from("zones").select("*"),
      supabase.from("service_types").select("*").eq("is_active", true).order("sort_order"),
      supabase.from("zone_pricing").select("zone_id"),
    ]);
    setRows(z.data || []);
    setServiceTypes(st.data || []);
    const counts: Record<string, number> = {};
    (pc.data || []).forEach((p: any) => { counts[p.zone_id] = (counts[p.zone_id] || 0) + 1; });
    setPricingCounts(counts);
  };

  useEffect(() => { load(); }, []);

  // Zone CRUD
  const handleSave = async () => {
    const payload = { code: form.code, name: form.name };
    if (editingId) {
      const { error } = await supabase.from("zones").update(payload).eq("id", editingId);
      if (error) { toast({ title: "Error", description: error.message, variant: "destructive" }); return; }
    } else {
      const { error } = await supabase.from("zones").insert(payload);
      if (error) { toast({ title: "Error", description: error.message, variant: "destructive" }); return; }
    }
    setForm({ code: "", name: "" });
    setEditingId(null);
    setIsDialogOpen(false);
    load();
    toast({ title: editingId ? "Actualizado" : "Creado" });
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    await supabase.from("zones").delete().eq("id", deleteId);
    setDeleteId(null);
    load();
  };

  const openCreate = () => { setForm({ code: "", name: "" }); setEditingId(null); setIsDialogOpen(true); };
  const openEdit = (row: any) => { setForm({ code: row.code, name: row.name }); setEditingId(row.id); setIsDialogOpen(true); };

  // Pricing CRUD
  const openPricing = async (zone: any) => {
    setSelectedZone(zone);
    const { data } = await supabase.from("zone_pricing").select("*, service_types(name)").eq("zone_id", zone.id);
    setPricingRows(data || []);
  };

  const loadPricing = async () => {
    if (!selectedZone) return;
    const { data } = await supabase.from("zone_pricing").select("*, service_types(name)").eq("zone_id", selectedZone.id);
    setPricingRows(data || []);
    load(); // refresh counts
  };

  const openPricingCreate = () => {
    setPricingForm({ service_type_id: "", price: "", max_passengers: "4", extra_cost_per_pax: "0" });
    setPricingEditingId(null);
    setIsPricingFormOpen(true);
  };

  const openPricingEdit = (row: any) => {
    setPricingForm({
      service_type_id: row.service_type_id,
      price: String(row.price),
      max_passengers: String(row.max_passengers),
      extra_cost_per_pax: String(row.extra_cost_per_pax),
    });
    setPricingEditingId(row.id);
    setIsPricingFormOpen(true);
  };

  const handlePricingSave = async () => {
    if (!selectedZone) return;
    const payload = {
      zone_id: selectedZone.id,
      service_type_id: pricingForm.service_type_id,
      price: Number(pricingForm.price),
      max_passengers: Number(pricingForm.max_passengers),
      extra_cost_per_pax: Number(pricingForm.extra_cost_per_pax),
    };
    if (pricingEditingId) {
      const { error } = await supabase.from("zone_pricing").update(payload).eq("id", pricingEditingId);
      if (error) { toast({ title: "Error", description: error.message, variant: "destructive" }); return; }
    } else {
      const { error } = await supabase.from("zone_pricing").insert(payload);
      if (error) { toast({ title: "Error", description: error.message, variant: "destructive" }); return; }
    }
    setIsPricingFormOpen(false);
    setPricingEditingId(null);
    loadPricing();
    toast({ title: pricingEditingId ? "Precio actualizado" : "Precio creado" });
  };

  const handlePricingDelete = async () => {
    if (!pricingDeleteId) return;
    await supabase.from("zone_pricing").delete().eq("id", pricingDeleteId);
    setPricingDeleteId(null);
    loadPricing();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold">Zonas</h2>
          <p className="text-muted-foreground text-sm mt-1">Zonas y precios por tipo de servicio <Badge variant="secondary" className="ml-2">{rows.length}</Badge></p>
        </div>
        <Button onClick={openCreate}><Plus className="h-4 w-4 mr-1" /> Agregar</Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {rows.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <MapPin className="h-10 w-10 mb-3 opacity-40" /><p>No hay zonas registradas</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Código</TableHead>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Precios</TableHead>
                  <TableHead className="w-[140px] text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map(row => (
                  <TableRow key={row.id}>
                    <TableCell className="font-mono">{row.code}</TableCell>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>
                      <Badge variant="secondary">{pricingCounts[row.id] || 0}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="icon" variant="ghost" onClick={() => openEdit(row)} title="Editar zona"><Pencil className="h-4 w-4" /></Button>
                        <Button size="icon" variant="ghost" onClick={() => openPricing(row)} title="Ver precios"><DollarSign className="h-4 w-4" /></Button>
                        <Button size="icon" variant="ghost" className="text-destructive" onClick={() => setDeleteId(row.id)} title="Eliminar"><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Zone create/edit dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingId ? "Editar Zona" : "Nueva Zona"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label>Código</Label><Input value={form.code} onChange={e => setForm({ ...form, code: e.target.value })} /></div>
            <div><Label>Nombre</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave}>{editingId ? "Actualizar" : "Crear"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Zone delete confirm */}
      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>¿Eliminar zona?</AlertDialogTitle><AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={handleDelete}>Eliminar</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Pricing dialog for selected zone */}
      <Dialog open={!!selectedZone} onOpenChange={(open) => { if (!open) { setSelectedZone(null); setIsPricingFormOpen(false); } }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Precios — {selectedZone?.name} <Badge variant="outline" className="ml-2">{selectedZone?.code}</Badge></DialogTitle>
          </DialogHeader>

          {!isPricingFormOpen ? (
            <div>
              <div className="flex justify-end mb-3">
                <Button size="sm" onClick={openPricingCreate}><Plus className="h-4 w-4 mr-1" /> Agregar Precio</Button>
              </div>
              {pricingRows.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No hay precios configurados para esta zona</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tipo Servicio</TableHead>
                      <TableHead>Precio Base</TableHead>
                      <TableHead>Max Pax</TableHead>
                      <TableHead>Extra/Pax</TableHead>
                      <TableHead className="w-[80px] text-right">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pricingRows.map(row => (
                      <TableRow key={row.id}>
                        <TableCell>{(row.service_types as any)?.name}</TableCell>
                        <TableCell className="font-semibold text-green-700">${row.price}</TableCell>
                        <TableCell>{row.max_passengers}</TableCell>
                        <TableCell>${row.extra_cost_per_pax}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button size="icon" variant="ghost" onClick={() => openPricingEdit(row)}><Pencil className="h-4 w-4" /></Button>
                            <Button size="icon" variant="ghost" className="text-destructive" onClick={() => setPricingDeleteId(row.id)}><Trash2 className="h-4 w-4" /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          ) : (
            <div className="space-y-4 py-2">
              <div>
                <Label>Tipo de Servicio</Label>
                <Select value={pricingForm.service_type_id} onValueChange={v => setPricingForm({ ...pricingForm, service_type_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar tipo de servicio" /></SelectTrigger>
                  <SelectContent>{serviceTypes.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Precio Base ($)</Label><Input type="number" step="0.01" value={pricingForm.price} onChange={e => setPricingForm({ ...pricingForm, price: e.target.value })} /></div>
              <div><Label>Max Pasajeros</Label><Input type="number" min="1" value={pricingForm.max_passengers} onChange={e => setPricingForm({ ...pricingForm, max_passengers: e.target.value })} /></div>
              <div><Label>Costo Extra por Pasajero ($)</Label><Input type="number" step="0.01" min="0" value={pricingForm.extra_cost_per_pax} onChange={e => setPricingForm({ ...pricingForm, extra_cost_per_pax: e.target.value })} /></div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setIsPricingFormOpen(false)}>Cancelar</Button>
                <Button onClick={handlePricingSave}>{pricingEditingId ? "Actualizar" : "Crear"}</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Pricing delete confirm */}
      <AlertDialog open={!!pricingDeleteId} onOpenChange={(open) => !open && setPricingDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>¿Eliminar precio?</AlertDialogTitle><AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={handlePricingDelete}>Eliminar</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
